import React, { useEffect, useState, useCallback } from 'react';
import { getInvoices, getFilterOptions, downloadExport } from '../lib/api';
import { formatCurrency, formatDate, getBiltyStatusColor } from '../lib/utils';
import { Search, Filter, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, FileSpreadsheet, Eye, Download, Loader2 } from 'lucide-react';
import { Link, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';

export default function InvoicesPage() {
  const [searchParams] = useSearchParams();
  const initialBiltyStatus = searchParams.get('bilty_status') || '';

  const [invoices, setInvoices] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [pageInput, setPageInput] = useState('1');
  const [search, setSearch] = useState('');
  const [filters, setFilters] = useState({ series: '', fy: '', bilty_status: initialBiltyStatus, salesman: '' });
  const [filterOptions, setFilterOptions] = useState({});
  const [loading, setLoading] = useState(true);
  // Deep-linked from the Dashboard's KPI cards (e.g. Pending Bilties) with a
  // filter preset — start the panel open so the active filter isn't hidden.
  const [showFilters, setShowFilters] = useState(!!initialBiltyStatus);
  const [exporting, setExporting] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getInvoices({ page, limit: 25, search, ...filters });
      setInvoices(res.data.invoices || []);
      setTotal(res.data.total || 0);
      setPages(res.data.pages || 1);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [page, search, filters]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setPageInput(String(page)); }, [page]);

  const goToPage = (e) => {
    e.preventDefault();
    const n = parseInt(pageInput, 10);
    if (Number.isFinite(n) && n >= 1 && n <= pages) setPage(n);
    else setPageInput(String(page));
  };
  useEffect(() => { getFilterOptions().then(r => setFilterOptions(r.data)).catch(() => {}); }, []);

  const handleExport = async (format) => {
    setExporting(format);
    try {
      const params = { ...filters, search };
      const url = format === 'csv' 
        ? `/api/boss/fa/invoices/export/csv` 
        : `/api/boss/fa/invoices/export/excel`;
      const queryStr = new URLSearchParams(params).toString();
      const filename = format === 'csv' 
        ? `invoices_export_${new Date().toISOString().slice(0,10)}.csv`
        : `invoices_export_${new Date().toISOString().slice(0,10)}.xlsx`;
      const success = await downloadExport(`${url}?${queryStr}`, filename);
      if (success) {
        toast.success(`Invoice register exported as ${format.toUpperCase()}`);
      } else {
        toast.error('Export failed. Please try again.');
      }
    } catch (err) {
      toast.error('Export failed');
    }
    setExporting('');
  };

  return (
    <div className="space-y-4" data-testid="invoices-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-boss-text">Invoice Register</h1>
          <p className="text-boss-text-muted text-sm">{total} invoices found</p>
        </div>
        <div className="flex items-center gap-2" data-testid="invoice-export-buttons">
          <button
            onClick={() => handleExport('csv')}
            disabled={!!exporting}
            className="flex items-center gap-2 px-3 py-2 rounded-lg border border-boss-border text-sm hover:bg-boss-surface-2 disabled:opacity-50 transition-colors"
            data-testid="export-csv-btn"
          >
            {exporting === 'csv' ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />}
            Export CSV
          </button>
          <button
            onClick={() => handleExport('excel')}
            disabled={!!exporting}
            className="flex items-center gap-2 px-3 py-2 rounded-lg bg-boss-violet text-white text-sm hover:bg-[#5B4CF2] disabled:opacity-50 transition-colors"
            data-testid="export-excel-btn"
          >
            {exporting === 'excel' ? <Loader2 size={14} className="animate-spin" /> : <FileSpreadsheet size={14} />}
            Export Excel
          </button>
        </div>
      </div>

      {/* Search & Filters */}
      <div className="bg-white rounded-xl p-4 shadow-sm border border-boss-border">
        <div className="flex flex-wrap gap-3">
          <div className="relative flex-1 min-w-[200px]">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-boss-text-muted" />
            <input
              type="text" placeholder="Search bill no, party, salesman..."
              value={search} onChange={e => { setSearch(e.target.value); setPage(1); }}
              className="w-full pl-9 pr-4 py-2 rounded-lg border border-boss-border text-sm focus:ring-2 focus:ring-boss-violet/30 focus:outline-none"
              data-testid="invoice-search"
            />
          </div>
          <button onClick={() => setShowFilters(!showFilters)} className="px-4 py-2 rounded-lg border border-boss-border text-sm flex items-center gap-2 hover:bg-boss-surface-2">
            <Filter size={14} /> Filters
          </button>
        </div>
        {showFilters && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-3 pt-3 border-t border-boss-border">
            <select value={filters.series} onChange={e => { setFilters(f => ({...f, series: e.target.value})); setPage(1); }} className="px-3 py-2 rounded-lg border border-boss-border text-sm" data-testid="filter-series">
              <option value="">All Series</option>
              {(filterOptions.series || []).map(s => <option key={s} value={s}>{s}</option>)}
            </select>
            <select value={filters.fy} onChange={e => { setFilters(f => ({...f, fy: e.target.value})); setPage(1); }} className="px-3 py-2 rounded-lg border border-boss-border text-sm">
              <option value="">All FY</option>
              {(filterOptions.financial_years || []).map(s => <option key={s} value={s}>{s}</option>)}
            </select>
            <select value={filters.bilty_status} onChange={e => { setFilters(f => ({...f, bilty_status: e.target.value})); setPage(1); }} className="px-3 py-2 rounded-lg border border-boss-border text-sm">
              <option value="">All Bilty Status</option>
              <option value="pending">Pending</option>
              <option value="uploaded">Uploaded</option>
              <option value="verified">Verified</option>
            </select>
            <select value={filters.salesman} onChange={e => { setFilters(f => ({...f, salesman: e.target.value})); setPage(1); }} className="px-3 py-2 rounded-lg border border-boss-border text-sm">
              <option value="">All Salesmen</option>
              {(filterOptions.salesmen || []).map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
        )}
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl shadow-sm border border-boss-border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="invoices-table">
            <thead>
              <tr className="border-b border-boss-border bg-boss-surface-2">
                <th className="text-left py-3 px-4 font-medium text-boss-text-muted">Bill No.</th>
                <th className="text-left py-3 px-4 font-medium text-boss-text-muted">Date</th>
                <th className="text-left py-3 px-4 font-medium text-boss-text-muted">Party</th>
                <th className="text-left py-3 px-4 font-medium text-boss-text-muted">Salesman</th>
                <th className="text-left py-3 px-4 font-medium text-boss-text-muted">Transport</th>
                <th className="text-left py-3 px-4 font-medium text-boss-text-muted">Station</th>
                <th className="text-right py-3 px-4 font-medium text-boss-text-muted">Amount</th>
                <th className="text-center py-3 px-4 font-medium text-boss-text-muted">Bilty</th>
                <th className="text-center py-3 px-4 font-medium text-boss-text-muted">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                [...Array(8)].map((_, i) => (
                  <tr key={i} className="border-b border-boss-border/50">
                    {[...Array(9)].map((_, j) => <td key={j} className="py-3 px-4"><div className="h-4 bg-boss-surface-3 rounded animate-pulse" /></td>)}
                  </tr>
                ))
              ) : invoices.length === 0 ? (
                <tr><td colSpan={9} className="py-12 text-center text-boss-text-muted">No invoices found. Import data to get started.</td></tr>
              ) : (
                invoices.map((inv) => (
                  <tr key={inv._id} className="border-b border-boss-border/50 hover:bg-boss-surface-2/50 transition-colors" data-testid={`invoice-row-${inv._id}`}>
                    <td className="py-2.5 px-4 font-mono text-xs font-medium">{inv.bill_no_full}</td>
                    <td className="py-2.5 px-4 text-boss-text-muted">{formatDate(inv.bill_date)}</td>
                    <td className="py-2.5 px-4 max-w-[160px] truncate" title={inv.party_name}>{inv.party_name}</td>
                    <td className="py-2.5 px-4">{inv.salesman_name}</td>
                    <td className="py-2.5 px-4">{inv.transport}</td>
                    <td className="py-2.5 px-4">{inv.station}</td>
                    <td className="py-2.5 px-4 text-right font-mono">{formatCurrency(inv.amount_grand_total)}</td>
                    <td className="py-2.5 px-4 text-center">
                      <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${getBiltyStatusColor(inv.bilty_status)}`}>
                        {inv.bilty_status || 'pending'}
                      </span>
                    </td>
                    <td className="py-2.5 px-4 text-center">
                      <Link to={`/boss/fa/invoices/${inv._id}`} className="inline-flex items-center gap-1 text-boss-violet hover:underline text-xs">
                        <Eye size={14} /> View
                      </Link>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {pages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-boss-border">
            <span className="text-sm text-boss-text-muted">Page {page} of {pages} ({total} total)</span>
            <div className="flex items-center gap-2">
              <button disabled={page <= 1} onClick={() => setPage(1)} title="First page" className="p-2 rounded-lg border border-boss-border hover:bg-boss-surface-2 disabled:opacity-30">
                <ChevronsLeft size={16} />
              </button>
              <button disabled={page <= 1} onClick={() => setPage(p => p - 1)} title="Previous page" className="p-2 rounded-lg border border-boss-border hover:bg-boss-surface-2 disabled:opacity-30">
                <ChevronLeft size={16} />
              </button>
              {/* Prev/next alone is impractical once a large tenant's register
                  spans hundreds of pages — a direct jump is the actual fix. */}
              <form onSubmit={goToPage} className="flex items-center gap-1.5 text-sm">
                <input
                  type="number" min={1} max={pages} value={pageInput}
                  onChange={e => setPageInput(e.target.value)}
                  className="w-14 px-2 py-1.5 rounded-lg border border-boss-border text-sm text-center focus:ring-2 focus:ring-boss-violet/30 focus:outline-none"
                  data-testid="invoice-page-input"
                />
                <span className="text-boss-text-muted">/ {pages}</span>
                <button type="submit" className="px-2 py-1.5 rounded-lg border border-boss-border text-xs hover:bg-boss-surface-2">Go</button>
              </form>
              <button disabled={page >= pages} onClick={() => setPage(p => p + 1)} title="Next page" className="p-2 rounded-lg border border-boss-border hover:bg-boss-surface-2 disabled:opacity-30">
                <ChevronRight size={16} />
              </button>
              <button disabled={page >= pages} onClick={() => setPage(pages)} title="Last page" className="p-2 rounded-lg border border-boss-border hover:bg-boss-surface-2 disabled:opacity-30">
                <ChevronsRight size={16} />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
